import React from 'react';
import ReactSlider from 'react-slider';

import { ApiQueryParams } from '../../../interfaces';
import { LinkButtonTextField } from '../../LinkButtonTextField';

export interface DistanceSliderStateProps {
    id: string;
    label: string;
    min: number;
    max: number;
    initialDistance?: number;
}

export interface DistanceSliderDispatchProps {
    distanceChange: (newDistance?: number) => void;
    triggerFetch: (queryParam?: ApiQueryParams) => void;
}

type SliderProps = DistanceSliderStateProps & DistanceSliderDispatchProps;

export const DistanceSlider: React.FC<SliderProps> = ({
    id,
    label,
    min,
    max,
    initialDistance,
    distanceChange,
    triggerFetch,
}): React.ReactElement<SliderProps> => {
    const onlyNumbers = /[0-9]+/g;
    const [showInput, setShowInput] = React.useState<boolean>(false);
    const [distance, setDistance] = React.useState<number>(initialDistance || max);
    // eslint-disable-next-line no-undef
    const valueChangeTimeout = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect((): void => {
        setDistance(initialDistance || max);
    }, [initialDistance, max]);

    const updateState = (value: number): void => {
        if (value !== max) {
            distanceChange(value);
        } else {
            distanceChange(undefined);
        }
        triggerFetch();
    };

    const onAfterChange = (value: number): void => {
        if (valueChangeTimeout.current) {
            clearTimeout(valueChangeTimeout.current);
        }
        valueChangeTimeout.current = setTimeout((): void => {
            updateState(value);
        }, 400);

        setDistance(value);
    };

    const onBlur = (): void => {
        setShowInput(false);
        updateState(distance);
    };

    const onClick = (): void => setShowInput(true);

    const onTextChange = (value: string): void => {
        setDistance(Math.min(Math.max(~~value, min), max));
    };

    const preventNonNumbers = (event: React.KeyboardEvent): void => {
        if (!onlyNumbers.test(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <div className="u-mv16">
            <h3 className="u-t5">{label}</h3>
            <div>
                <span data-testid={`DistanceSliderText-Min-${id}`}>{min} - </span>
                <LinkButtonTextField
                    id={id}
                    label={`Maks ${label}`}
                    type="number"
                    value={distance.toString()}
                    metric="m"
                    prefix={distance === max ? 'over' : undefined}
                    showInput={showInput}
                    onClick={onClick}
                    onChange={(event): void => onTextChange(event.target.value)}
                    onBlur={onBlur}
                    onKeyPress={preventNonNumbers}
                    data-testid={`DistanceSliderText-Max-${id}`}
                />
            </div>
            <div data-testid={`DistanceSliderWrapper-${id}`}>
                <ReactSlider
                    ariaLabel={`Maks ${label.toLowerCase()}`}
                    // TODO should there be aria at all here as the values are shown above
                    // ariaValuetext={distance.toString()}
                    min={min}
                    max={max}
                    value={distance}
                    step={10}
                    onChange={setDistance}
                    onAfterChange={onAfterChange}
                    className="slider"
                    thumbClassName="slider__thumb"
                    trackClassName="slider__track-active"
                    data-testid={`DistanceSlider-${id}`}
                />
            </div>
        </div>
    );
};
